import { FunctionComponent, useState } from "react";
import { useForm } from "react-hook-form";
import { ClientRegistrationForm, EmploymentInformationForm, FinancialHistoryForm, FinancialKnowldgeForm, LendingPurposeForm } from "src/components/forms";
import ConfirmFormSubmission from "src/components/forms/NEATT/ConfirmFormSubmission";
import FinancialGoalsForm from "src/components/forms/NEATT/FinancialGoalsForm";
import { Button, PageContainer, PageTitle } from "src/components/generic";
import TopNav from "src/components/navigation/TopNav";
import { auth } from "src/utils";
import { validateEmail, validatePassword } from "src/utils/format/strings";
import { addCreditUser } from "src/utils/informativ/creditUsers";
import { BUTTON_THEME_TYPES } from "src/utils/types/ButtonTypes";
import { extractInformativError, extractCusId } from "src/utils/informativ/validateResponse";
import convert from "xml-js";
import { addNewCustomer } from "src/utils/stripe/customers";
import { userDataToClient } from "src/utils/format/users";
import { putUser } from "src/utils/dynamo/mutations";
import ConfirmAccount from "./ConfirmAccount";

enum StepOptions {
    BASIC = "BASIC",
    KNOWLEDGE = "KNOWLEDGE",
    EMPLOYMENT = "EMPLOYMENT",
    LOAN = "LOAN",
    HISTORY = "HISTORY",
    GOALS = "GOALS",
    CONFIRM = "CONFIRM",
    VERIFY = "VERIFY"
}

interface IClientOnboardingFormsProps {
    skipPayment?: boolean;
}

const ClientOnboardingForms: FunctionComponent<IClientOnboardingFormsProps> = ({ skipPayment }) => {
    const [error, setError] = useState<string[]>([]);
    const [currentStep, setCurrentStep] = useState<StepOptions>(StepOptions.BASIC);
    const [user, setUser] = useState<Record<any, any>>();

    const goNextStep = () => {
    switch (currentStep) {
        case StepOptions.BASIC:
            setCurrentStep(StepOptions.KNOWLEDGE);
            break;
        case StepOptions.KNOWLEDGE:
            setCurrentStep(StepOptions.EMPLOYMENT);
            break;
        case StepOptions.EMPLOYMENT:
            setCurrentStep(StepOptions.LOAN);
            break;
        case StepOptions.LOAN:
            setCurrentStep(StepOptions.HISTORY);
            break;
        case StepOptions.HISTORY:
            setCurrentStep(StepOptions.GOALS);
            break;
        case StepOptions.GOALS:
            setCurrentStep(StepOptions.CONFIRM);
            break;
        case StepOptions.CONFIRM:
            setCurrentStep(StepOptions.VERIFY)
            break;
        }
    };

    const goBackStep = () => {
    switch (currentStep) {
        case StepOptions.KNOWLEDGE:
            setCurrentStep(StepOptions.BASIC);
            break;
        case StepOptions.EMPLOYMENT:
            setCurrentStep(StepOptions.KNOWLEDGE);
            break;
        case StepOptions.LOAN:
            setCurrentStep(StepOptions.EMPLOYMENT);
            break;
        case StepOptions.HISTORY:
            setCurrentStep(StepOptions.LOAN);
            break;
        case StepOptions.GOALS:
            setCurrentStep(StepOptions.HISTORY);
            break;
        case StepOptions.CONFIRM:
            setCurrentStep(StepOptions.GOALS);
            break;
        }
    };

    const isFirstStep: boolean = currentStep === StepOptions.BASIC;
    const isLastStep: boolean = currentStep === StepOptions.CONFIRM;

    const {
        getValues,
        register,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: "",
            email: "",
            dob: "",
            password: "",
            phone: "",
            address: "",
            city: "",
            state: "",
            zip: "",
            gender: "MALE",
            termsAndConditions: false,
            familiarWithD2I: "",
            financialPlanInPlace: "",
            experienceLevel: "",
            attendedCoursesOrWorkshops: "",
            workedWithAdvisor: "",
            savingForRetirement: "",
            amountPerMonth: "",
            usingToolsOrApps: "",
            livingStatus: "",
            addressDuration: "",
            changesToIncomeOverYear: "",
            documentationConsent: "",
            additionalIncome: "",
            employmentStatus: "",
            employmentDuration: "",
            openToExploring: "",
            governmentBackedLoans: "",
            activeConcerns: "",
            reasonforconcerns: "",
            loanType: "",
            seekingLoan: "",
            loanamount: "",
            collectionsOrJudgements: "",
            behindOnPayments: "",
            using30Percent: "",
            awareOfCreditScore: "",
            placedBlockOnCredit: "",
            studentLoanDebt: "",
            checkedCreditInLastYear: "",
            cosignerOrGuaranteeForClient: "",
            cosignerOrGuaranteeForOther: "",
            foreclosureOrDefault: "",
            reason: "",
            applied: "",
            denied: "",
            primaryGoal: "",
            experiencedHarships: "",
            percentageIncome: "",
            stepsToImprove: "",
            howToHear: "",
            timeline: "",
            lifeChanges: "",
            lifeType: "",
            asideMoney: "",
            supportSystem: "",
            largeExpense: "",
            dependents: "",
            paying: "",
            payType: "",
        },
        // resolver: yupResolver(forms.schema.ClientRegistrationSchema),
    });
    const formValues = getValues()
    const saving = watch("savingForRetirement")?.toLowerCase();
    const employment = watch("employmentStatus");
    const seekingLoan = watch("seekingLoan");
    const concernedAboutProcess = watch("activeConcerns");
    const appliedForLoan = watch("applied");
    const foreclose = watch("foreclosureOrDefault");
    const isPaying = watch("paying");
    const expectingChange = watch("lifeChanges");

    const submit = async () => {
        try {
            const passwordMessage = "Password Invalid. Must be 8 characters, include at least one Number and one Character";
            const emailMessage = "Email Required";
            const phoneMessage = "Phone Number Required"
            const {email, password, name, phone} = formValues;
            const passwordIsValid = validatePassword(password);
            const emailIsValid = validateEmail(email);

            setError([
                ...error, 
                (!passwordIsValid && !error.includes(passwordMessage)) ? passwordMessage : "",
                (!emailIsValid && !error.includes(emailMessage)) ? emailMessage : "",
                (!phone && !error.includes(phoneMessage) ? phoneMessage : "")
            ]);

            if(!passwordIsValid || !emailIsValid) return;

            const cognitoResponse = await auth.cognito.registerCognitoUser(email, password);
            const uid: string = cognitoResponse.userSub;
            
            console.log({uid});
            if(!uid) return setError(["Cognito Error, Account Creation Failed"]);

            const ip = await auth.resolveClientIp();
            console.log({ip});
            const informativResult = await addCreditUser({
                uid,
                name,
                email,
                password,
                ip,
            });
            console.log({
                uid,
                name,
                email,
                password,
                ip,
            });
            const convertedResult = convert.xml2js(informativResult, { compact: true }); 
            // Explicitly check the CBC ERROR tag before treating this as a success
            const informativError = extractInformativError(convertedResult as any);
            if (informativError) {
                setError(["CBC Error, Account Not Verified"])
                throw new Error(informativError);
            }
            
            const cusId = extractCusId(convertedResult as any);
            if(!cusId) setError(["Customer ID Failed Generation"]);

            let customer;
            customer = await addNewCustomer(name, email, phone);
            console.log({customer})
            if(!customer || !customer.id) return setError(["Stripe Error"]);

            const newClient = userDataToClient(formValues, uid, "N/A", "N/A");
            // newClient.metadata.cus_id = cusId || "";
            newClient.metadata.stripeId = customer?.id;
        
            await putUser(newClient);
            setUser(newClient);
            localStorage.setItem("user", JSON.stringify(newClient));

            goNextStep();
        } catch(error) {
            console.log({error});
        }
    }

    return (
        <PageContainer>
            <TopNav />

            <div>
                <PageTitle title={`${currentStep} Form`} />
                {error?.map((message:string) => <p>{message}</p>)}
                {
                    isFirstStep ? <ClientRegistrationForm register={register} errors={errors} /> :
                    currentStep === StepOptions.KNOWLEDGE ? <FinancialKnowldgeForm  register={register} errors={errors} saving={saving} /> :
                    currentStep === StepOptions.EMPLOYMENT ? <EmploymentInformationForm register={register} errors={errors} employment={employment} /> :
                    currentStep === StepOptions.LOAN ? <LendingPurposeForm register={register} errors={errors} seekingLoan={seekingLoan} concernedAboutProcess={concernedAboutProcess} /> :
                    currentStep === StepOptions.HISTORY ? <FinancialHistoryForm register={register} errors={errors} appliedForLoan={appliedForLoan} foreclose={foreclose} /> : 
                    currentStep === StepOptions.GOALS ? <FinancialGoalsForm register={register} errors={errors} isPaying={isPaying} expectingChange={expectingChange} /> : 
                    currentStep === StepOptions.CONFIRM ? <ConfirmFormSubmission values={formValues} /> :
                    <ConfirmAccount email={formValues.email} tier={'bronze'} cusId={user?.metadata.stripeId} />
                }
            </div>

                {
                    !(currentStep  === StepOptions.VERIFY) && (
                        <div className="w-full flex items-center justify-around pt-8">
                            <Button text="Previous" onClick={goBackStep} disabled={isFirstStep} theme={BUTTON_THEME_TYPES.PRIMARY_INVERSE} />

                            <Button text={isLastStep ? "Submit" : "Next"} onClick={isLastStep ? submit : goNextStep} />
                        </div>
                    )
                }
        </PageContainer>
    )
}

export default ClientOnboardingForms;